/**
 * Portal-side gate. The admin LoginGate handles the *AuthN* layer (Auth0
 * magic link); this component layers the *AuthZ* check on top — only renders
 * children when the JWT belongs to an active registered partner.
 *
 * Three failure modes have distinct UX:
 *   - forbidden: shows a "not registered" panel with a sign-out link
 *   - error: shows a retry button (network glitch, 5xx)
 *   - loading: just a spinner — keep it brief
 */

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link as MuiLink,
  Stack,
  Typography,
} from "@mui/material";
import { type ReactNode, createContext, useContext } from "react";

import { useAuth } from "../../../shared/providers/AuthProvider";
import { adminPortalUrl } from "../../../lib/hosts";
import { useI18n } from "../../../shared/providers/I18nProvider";
import { usePartnerProfile } from "../hooks/usePartnerProfile";
import type { PartnerProfile } from "../types";

const PartnerProfileContext = createContext<PartnerProfile | null>(null);

export function usePartner(): PartnerProfile {
  const ctx = useContext(PartnerProfileContext);
  if (!ctx) throw new Error("usePartner() must be used inside PortalGate");
  return ctx;
}

export function PortalGate({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const profile = usePartnerProfile();
  const { logout } = useAuth();

  if (profile.status === "loading") {
    return (
      <Centered>
        <CircularProgress size={36} />
      </Centered>
    );
  }

  if (profile.status === "forbidden") {
    return (
      <Centered>
        <Brand t={t} />
        <Box sx={{ maxWidth: 420, mt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("portal.gate.notRegistered")}
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("portal.gate.mistakePrefix")}{" "}
            <MuiLink href="mailto:partners@paybyrd.com">partners@paybyrd.com</MuiLink>{" "}
            {t("portal.gate.mistakeSuffix")}
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <Button variant="outlined" onClick={() => logout()}>
              {t("portal.gate.signOut")}
            </Button>
            <Button variant="text" component="a" href={adminPortalUrl("/")}>
              {t("portal.gate.goToAdmin")}
            </Button>
          </Stack>
        </Box>
      </Centered>
    );
  }

  if (profile.status === "error") {
    return (
      <Centered>
        <Brand t={t} />
        <Alert severity="error" sx={{ mt: 2, maxWidth: 420 }}>
          {t("portal.gate.loadError")}: {profile.message}
        </Alert>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={profile.refetch}>
          {t("portal.gate.tryAgain")}
        </Button>
      </Centered>
    );
  }

  return (
    <PartnerProfileContext.Provider value={profile.profile}>
      {children}
    </PartnerProfileContext.Provider>
  );
}

function Brand({ t }: { t: (key: string) => string }) {
  return (
    <Stack alignItems="center" spacing={1.5}>
      {/* Real Paybyrd wordmark for the portal-gate splash + error states. */}
      <Box
        component="img"
        src="/paybyrd-wordmark.svg"
        alt="Paybyrd"
        sx={{ height: 32, width: "auto", display: "block" }}
      />
      <Typography
        variant="caption"
        sx={{
          fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
          fontSize: 11,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          fontWeight: 600,
          color: "primary.main",
        }}
      >
        {t("portal.gate.partnersPortal")}
      </Typography>
    </Stack>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ minHeight: "100vh", p: 3, bgcolor: "background.default" }}
      spacing={1}
    >
      {children}
    </Stack>
  );
}
